import Vue from 'vue'
import axios from 'axios'
import { ErrorWrapper } from './util'
import $store from '../store'
import $router from '../router'
import { BaseService } from '@/services/base.service'

export class AuthService {
  static async makeLogin (params) {
    try {
      const response = await BaseService.request().post('v1/gateway/user/login', params)
      return response.data
    } catch (error) {
      return error.response.data
    }
  }

  static async makeLogout (params) {
    try {
      const response = await BaseService.request().post('v1/gateway/user/logout', params)
      return response.data
    } catch (error) {
      return error.response.data
    }
  }
  
  static async getUserInfo () {
    try {
      const response = await BaseService.request().get('v1/gateway/user/info')
      return response.data
    } catch (error) {
      return error.response.data
    }
  }

  static async getVerifyToken () {
    try {
      const response = await BaseService.request().get('v1/gateway/user/verify-token')
      return response.data
    } catch (error) {
      return error.response.data
    }
  }

  static async refreshTokens () {
    try {
      const response = await axios.post(`${process.env.VUE_APP_URL_GATEWAY}/v1/gateway/user/refresh-access-token`, {})
      if (response.code === '00') {
        AuthService._setAuthData({
          accessToken: response.data.token,
          idUserData: response.data
        })
      }
      return response
    } catch (error) {
      AuthService._resetAuthData()
      $router.push({ name: 'index' }).catch(() => {})
      throw new ErrorWrapper(error)
    }
  }

  static async makeForgetPassword (params) {
    try {
      const response = await BaseService.request().post('v1/gateway/user/forget-password', params)
      return response.data
    } catch (error) {
      return error.response.data
    }
  }

  static async makeResetPassword (params) {
    try {
      const response = await BaseService.request().post('v1/gateway/user/reset-password', params)
      return response.data
    } catch (error) {
      return error.response.data
    }
  }

  static async makeSignup (params) {
    try {
      const response = await BaseService.request().post('v1/gateway/user/register', params)
      return response.data
    } catch (error) {
      return error.response.data
    }
  }
  
  static async makeRegisterActive (params) {
    try {
      const response = await BaseService.request().post('v1/gateway/user/register-active', params)
      return response.data
    } catch (error) {
      return error.response.data
    }
  }

  static async makeChangeProfile (params) {
    try {
      const response = await BaseService.request().post('v1/gateway/user/change-profile', params)
      return response.data
    } catch (error) {
      return error.response.data
    }
  }
  
  static debounceRefreshTokens = this._debounce(() => {
    return this.refreshTokens()
  }, 100)

  static isAccessTokenExpired () {
    const accessTokenExpDate = localStorage.getItem('accessTokenExpDate')
    const nowTime = Math.floor(new Date().getTime() / 1000)
    return (accessTokenExpDate - nowTime > 0) ? 0 : 1
  }

  static setAccessTokenExpDate () {
    const accessTokenExpDate = Math.floor(new Date().getTime() / 1000) + 5 * 60 // 5 phut
    AuthService.setLocalStorage('accessTokenExpDate', accessTokenExpDate)
  }

  static hasRefreshToken () {
    return Boolean(AuthService.getLocalStorage('refreshToken'))
  }

  static setRefreshToken (status) {
    if (!['', 'true'].includes(status)) {
      throw new Error(`setRefreshToken: invalid value ${status}; Expect one of ['', 'true']`)
    }
    AuthService.setLocalStorage('refreshToken', status)
  }

  static getBearer () {
    return AuthService.getCookie('TID')
  }

  static setBearer (accessToken) {
    $store.commit('auth/SET_TOKEN', accessToken)
  }

  static getType () {
    return process.env.VUE_APP_APP_TYPE
  }

  static hasToken () {
    return Boolean(AuthService.getCookie('TID'))
  }

  static _debounce (inner, ms = 0) {
    let timer = null
    let resolves = []

    return function () {
      clearTimeout(timer)
      timer = setTimeout(() => {
        const result = inner()
        resolves.forEach(r => r(result))
        resolves = []
      }, ms)

      return new Promise(resolve => resolves.push(resolve))
    }
  }

  static _resetAuthData () {
    $store.commit('user/SET_CURRENT_USER', {})
    $store.commit('auth/SET_ATOKEN_EXP_DATE', null)
    $store.commit('auth/SET_TOKEN', null)
    AuthService.setRefreshToken('')
    AuthService.setBearer('')
  }

  static _setAuthData ({ accessToken, idUserData } = {}) {
    AuthService.setRefreshToken('true')
    AuthService.setLocalStorage('idUserData', JSON.stringify(idUserData))
    AuthService.setCookie('TID', accessToken)
    AuthService.setBearer(accessToken)
    $store.commit('user/SET_CURRENT_USER', idUserData)
  }

  static _setAuthInit (token) {
    AuthService.setCookie('TID', token)
    AuthService.setBearer(token)
  }

  static setCookie (key, value) {
    Vue.$cookies.set(key, value)
  }

  static getCookie (key) {
    return Vue.$cookies.isKey(key) ? Vue.$cookies.get(key) : null
  }

  static removeCookie (key, domain) {
    return Vue.$cookies.remove(key, null, domain)
  }

  static setLocalStorage (key, value) {
    localStorage.setItem(key, value)
  }

  static getLocalStorage (key) {
    return localStorage.getItem(key)
  }

  static removeLocalStorage (key) {
    localStorage.removeItem(key)
  }
}
